import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useToast } from "../components/ToastProvider";

const emptyForm = { username: "", password: "" };

export default function Users() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/admin/users");
      setRows(response?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = useMemo(() => rows.filter((r) => Number(r.isActive) === 1).length, [rows]);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/api/admin/users", form);
      toast.success("User created.");
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (userid) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await api.delete(`/api/admin/users/${userid}`);
      toast.success("User deactivated.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to deactivate user");
    }
  };

  return (
    <div className="rs-page">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Users</h1>
          <div className="rs-muted">{activeCount} active users</div>
        </div>
        <button className="rs-btn rs-btn--outline" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="rs-card">
        <div className="rs-card__header">
          <div className="rs-card__title">Create user</div>
          <div className="rs-card__subtitle">Admin users for this portal</div>
        </div>
        <form className="rs-form-inline" onSubmit={createUser}>
          <input
            className="rs-input"
            name="username"
            value={form.username}
            onChange={onChange}
            placeholder="Username / Email"
            required
          />
          <input
            className="rs-input"
            name="password"
            value={form.password}
            onChange={onChange}
            type="password"
            placeholder="Password"
            required
          />
          <button className="rs-btn rs-btn--primary" disabled={creating} type="submit">
            {creating ? "Saving..." : "Add"}
          </button>
        </form>
      </div>

      <div className="rs-card">
        <div className="rs-card__header">
          <div className="rs-card__title">All users</div>
        </div>
        <div className="rs-table-wrap">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Username</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="rs-empty">
                    {loading ? "Loading..." : "No users"}
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const isActive = Number(r.isActive) === 1;
                  return (
                    <tr key={r.userid}>
                      <td data-label="ID">
                        <div className="rs-td__value">{r.userid}</div>
                      </td>
                      <td data-label="Status">
                        <div className="rs-td__value">
                          {isActive ? (
                            <span className="rs-pill rs-pill--success">Active</span>
                          ) : (
                            <span className="rs-pill rs-pill--muted">Inactive</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Username">
                        <div className="rs-td__value">{r.username}</div>
                      </td>
                      <td data-label="Actions">
                        <div className="rs-td__value rs-actions">
                          {isActive ? (
                            <button className="rs-btn rs-btn--danger" onClick={() => deactivate(r.userid)} type="button">
                              Deactivate
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
